import {
  Action,
  OperationType,
  Position,
  RestApiPrefix,
  SignType,
  WarningType,
  WindowType
} from '../../dispatcher/Types';
import { 
  APP_SETTINGS 
} from '../../dispatcher/authentication/services/AuthSettings';

/**
 * Warn user that he/she will be logged out
 */
export const signOutWarning: Action = [{
  className: 'sm',
  items: [{
    operations: [],
    btnName: 'Auth.signOutWarning.close',
    btnConfig: {
      fillMode: 'flat',
      pos: Position.end
    }
  }, {
    operations: [{
      subType: SignType.request,
      type: OperationType.signOut
    }],
    btnName: 'Auth.signOutWarning.yes',
    btnConfig: {
      themeColor: 'error',
      pos: Position.end
    }
  }],
  warningType: WarningType.warning,
  text: 'Auth.signOutWarning.text',
  title: 'Auth.signOutWarning.title',
  type: OperationType.warning
}];

/**
 * signOutLocalUser request
 * Doesn't trigger global logout (end session) for all sso applications.
 * This will handle user management after user has been successfully signed out
 */
export const signOutLocalUserRequest: Action = [{
  windowType: WindowType.dialog,
  type: OperationType.closeWindow
}, {
  moduleName: 'AuthService',
  mappingName: 'proxy_logout_redirect_uri',
  name: 'getProxySignOutUrl',
  type: OperationType.addOperationData
}, {
  condition: '{proxy_logout_redirect_uri}',
  conditionOperations: [{
    moduleName: 'AuthService',
    name: 'proxySignedOutRedirectCallback'
  }],
  elseOperations: [{
    data: {
      method: 'post',
      url: APP_SETTINGS.logout_uri
    },
    handleError: true,
    type: OperationType.request
  }, {
    isCallback: false,
    isPromise: true,
    moduleName: 'AuthService',
    name: 'removeUser'
  }, {
    moduleName: 'AuthService',
    name: 'finishSignOut'
  }, {
    name: 'cleanup',
    type: OperationType.action
  }, {
    moduleName: 'AuthService',
    name: 'reload'
  }],
  type: OperationType.conditional
}];

/**
 * SignOut request
 * Can be local or SSO logout
 * This will handle user management after user has been successfully signed out
 */
export const signOutRequest: Action = [{
  windowType: WindowType.dialog,
  type: OperationType.closeWindow
}, {
  moduleName: 'AuthService',
  mappingName: 'post_logout_redirect_uri',
  name: 'getSignOutUrl',
  type: OperationType.addOperationData
}, {
  moduleName: 'AuthService',
  mappingName: 'proxy_logout_redirect_uri',
  name: 'getProxySignOutUrl',
  type: OperationType.addOperationData
}, {
  condition: '{proxy_logout_redirect_uri}',
  conditionOperations: [{
    moduleName: 'AuthService',
    name: 'proxySignedOutRedirectCallback'
  }],
  elseOperations: [{
    condition: '{post_logout_redirect_uri}',
    conditionOperations: [{
      data: {
        template: {
          method: 'get',
          usePrefix: RestApiPrefix.none,
          bind: {
            url: '{post_logout_redirect_uri}'
          }
        }
      },
      handleError: true,
      type: OperationType.request
    }],
    elseOperations: [{
      data: {
        method: 'post',
        url: APP_SETTINGS.logout_uri
      },
      handleError: true,
      type: OperationType.request
    }],
    type: OperationType.conditional
  }, {
    moduleName: 'AuthService',
    name: 'runService'
  }, {
    isCallback: false,
    isPromise: true,
    mappingName: 'user',
    moduleName: 'AuthService',
    name: 'getUser'
  }, {
    args: ['user'],
    moduleName: 'AuthService',
    name: 'isLocalUser',
    type: OperationType.addOperationData
  }, {
    moduleName: 'AuthService',
    name: 'isGlobalLogoutEnabled',
    type: OperationType.addOperationData
  }, {
    condition: '!{isLocalUser} && {isGlobalLogoutEnabled}',
    conditionOperations: [{
      isCallback: false,
      isPromise: true,
      moduleName: 'AuthService',
      name: 'signOut'
    }],
    elseOperations: [{
      condition: '!{noReload}',
      conditionOperations: [{
        name: 'cleanup',
        type: OperationType.action
      }, {
        moduleName: 'AuthService',
        name: 'finishSignOut'
      }],
      type: OperationType.conditional
    }],
    type: OperationType.conditional
  }],
  type: OperationType.conditional
}];
/**
 * Signs out non-existing user
 */
export const signOutNonExistingUser: Action = [{
  windowType: WindowType.dialog,
  type: OperationType.closeWindow
}, {
  moduleName: 'AuthService',
  name: 'signOut'
}];

/**
 * Closes warning dialog and reload page
 */
export const signOutRequestLogOk: Action = [{
  windowType: WindowType.dialog,
  type: OperationType.closeWindow
}, {
  moduleName: 'AuthService',
  name: 'reload'
}];

/**
 * Auth server logout redirect callback
 * This will handle user management after user has been successfully signed out
 */
export const signedOutRedirectCallback: Action = [{
  moduleName: 'AuthService',
  name: 'runService'
}, {
  isCallback: false,
  isPromise: true,
  handleError: true,
  moduleName: 'AuthService',
  name: 'signedOutRedirectCallback'
}];

/**
 * Warn user that he/she has already been logged out
 * This can happen if user has signed out from other SSO enabled application
 */
export const signedOutWarning: Action = [{
  data: {
    loginUri: APP_SETTINGS.login_uri
  },
  type: OperationType.data
}, {
  condition: '!(new RegExp({loginUri}.replace("/", "") + "$").test({route()}))',
  type: OperationType.conditional,
  conditionOperations: [{
    windowType: WindowType.warning,
    type: OperationType.closeWindow
  }, {
    className: 'sm footer-centered',
    items: [{
      operations: [{
        name: 'finishSignOut',
        moduleName: 'AuthService'
      }],
      btnName: 'Auth.signedOutWarning.ok',
      btnConfig: {
        themeColor: 'primary',
        pos: Position.end
      }
    }],
    text: 'Auth.signedOutWarning.text',
    title: 'Auth.signedOutWarning.title',
    type: OperationType.warning,
    warningType: WarningType.warning
  }]
}];
